import type { ButtonHTMLAttributes } from 'react';

import { twm } from '../../../core/themes/tw-merge';
import type { IconType } from '../../types';
import { Spinner } from '../Spinner';

export type BaseIconButtonProps = {
  title?: ButtonHTMLAttributes<HTMLButtonElement>['title'];
  onClick?: () => void;
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
  icon: IconType;
  isDisabled?: boolean;
  isLoading?: boolean;
  variant: 'primary' | 'secondary' | 'tertiary' | 'error';
  size?: 'xsmall' | 'small' | 'medium';
  className?: string;
  onMouseDown?: ButtonHTMLAttributes<HTMLButtonElement>['onMouseDown'];
};

export const BaseIconButton = ({
  onClick,
  isDisabled,
  isLoading,
  type,
  icon: Icon,
  size = 'medium',
  variant,
  className,
  onMouseDown,
  title,
}: BaseIconButtonProps) => {
  return (
    <button
      className={twm(
        'relative rounded-md flex items-center justify-center cursor-pointer shrink-0',
        size === 'xsmall' && 'w-8 h-8',
        size === 'small' && 'w-10 h-10',
        size === 'medium' && 'w-12 h-12',
        variant === 'primary' && 'bg-surface-accent hover:bg-surface-accent-hover text-fg-on-accent',
        variant === 'secondary' &&
          'bg-surface-secondary hover:bg-surface-secondary-hover text-fg-secondary hover:text-fg-secondary-hover',
        variant === 'tertiary' && 'hover:bg-surface-primary-hover text-fg-secondary hover:text-fg-secondary-hover',
        variant === 'error' && 'bg-surface-error-primary hover:bg-surface-error-primary-hover text-fg-on-accent',
        isDisabled &&
          'cursor-not-allowed bg-surface-disabled hover:bg-surface-disabled text-text-disabled hover:text-text-disabled',
        className,
      )}
      onClick={onClick}
      type={type ?? 'button'}
      disabled={isDisabled || isLoading}
      onMouseDown={onMouseDown}
      title={title}
    >
      <Icon className={twm(isLoading && 'opacity-0')} />
      {isLoading ? (
        <div className="absolute top-0 bottom-0 left-0 right-0 flex items-center justify-center">
          <Spinner className={twm(size === 'small' && 'scale-80', size === 'xsmall' && 'scale-60')} />
        </div>
      ) : null}
    </button>
  );
};
